import { useNavigate } from 'react-router-dom';
import { dataHour } from '@app/format';
import { t } from '@app/i18n';
import { Alert, Button, Card } from '@zarya/ui';
import { VerdictBadge } from '../Decisions/VerdictBadge';
import { DecisionTargetCell } from '../Decisions/DecisionTargetCell';
import { decisionIdentity } from '../Decisions/vocabulario';
import type { Overview } from './Overview.api';
import { cardFailure, failureText } from './degradation';
import s from './Overview.module.css';

/**
 * Card **Últimas decisões** (FR-094): as 6 mais recentes, **Ver todas** para T16
 * e a linha que abre o detalhe da decisão.
 *
 * A linha navega para `/decisoes?drawer={request_id}` — o mesmo que o protótipo
 * faz (`data-pa="go" data-v="decisions"` com o índice da decisão): o detalhe abre
 * já dentro da lista, com o contexto das vizinhas. É também a convenção de drawer
 * do console (`?drawer=<id>` na rota da tela).
 */

/**
 * A coluna Quando mostra sempre a data e a hora (correção T7 do mrolim ao protótipo do aim-6ph.4). Só a hora fazia
 * uma decisão de ontem parecer de hoje: o cartão cobre as últimas 24 h.
 */
function when(iso: string): string {
  return dataHour(iso) ?? iso;
}

export interface LastDecisionsCardProps {
  overview: Overview;
}

export function LastDecisionsCard({ overview }: LastDecisionsCardProps) {
  const navigate = useNavigate();
  const falha = cardFailure(overview.failures, 'ultimas_decisoes');
  const decisoes = overview.last_decisions;

  return (
    <Card
      title={t('overview.lastDecisions.title')}
      toolbar={
        <Button variant="out" size="sm" onClick={() => navigate('/decisoes')}>
          {t('overview.lastDecisions.all')}
        </Button>
      }
      tip={t('overview.lastDecisions.tip')}
    >
      {falha ? <Alert tone="warning">{failureText(falha)}</Alert> : null}

      {!falha && decisoes.length === 0 ? (
        <p className={s.vazio}>{t('overview.lastDecisions.empty')}</p>
      ) : null}

      {!falha && decisoes.length > 0 ? (
        <div className={s.tabelaWrap}>
          <table className={s.tabela} aria-label={t('overview.lastDecisions.table.label')}>
            <thead>
              <tr>
                <th>{t('overview.lastDecisions.column.when')}</th>
                <th>{t('overview.lastDecisions.column.identity')}</th>
                <th>{t('overview.lastDecisions.column.person')}</th>
                <th>{t('overview.lastDecisions.column.tool')}</th>
                <th>{t('overview.lastDecisions.column.verdict')}</th>
              </tr>
            </thead>
            <tbody>
              {decisoes.map((decisao) => {
                const identidade = decisionIdentity(decisao);
                return (
                  <tr
                    key={decisao.request_id}
                    className={s.clicavel}
                    data-testid={`decisao-${decisao.request_id}`}
                    onClick={() => navigate(`/decisoes?drawer=${decisao.request_id}`)}
                  >
                    <td className={[s.mono, s.muted].join(' ')} data-testid="last-decision-when">
                      {when(decisao.ts)}
                    </td>
                    <td className={identidade.conhecida ? undefined : s.mono}>
                      {identidade.conhecida ? (
                        <b>{identidade.texto}</b>
                      ) : (
                        // Identidade desconhecida: o SPIFFE ID é a informação.
                        <span title={t('overview.lastDecisions.unknownIdentity')}>
                          {identidade.texto}
                        </span>
                      )}
                    </td>
                    <td>
                      {decisao.sub ? (
                        <>
                          {decisao.sub}
                          {decisao.profile ? (
                            <span className={s.muted}> · {decisao.profile}</span>
                          ) : null}
                        </>
                      ) : (
                        <span className={s.muted}>{t('overview.lastDecisions.noPerson')}</span>
                      )}
                    </td>
                    {/* DEF-34: numa delegação A2A a chamada citou um agente, não uma tool. */}
                    <td className={s.mono}>
                      <DecisionTargetCell decision={decisao} />
                    </td>
                    <td>
                      <VerdictBadge decision={decisao} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </Card>
  );
}
